import React, { useState, useEffect } from 'react';
import { Copy, ExternalLink } from 'lucide-react';
import SuperAdminNav from '../../components/ali/SuperAdminNav';
import { getSuperAdminEmail } from '../../lib/ali-super-admin-email';

const SuperAdminIntelligence = () => {
  const [intelligenceItems, setIntelligenceItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const email = getSuperAdminEmail();
        const url = email ? `/api/ali/super-admin/intelligence?email=${encodeURIComponent(email)}` : '/api/ali/super-admin/intelligence';
        const response = await fetch(url);
        const result = await response.json();
        if (result.ok) {
          setIntelligenceItems(result.items || []);
        }
      } catch (error) {
        console.error('Error fetching intelligence:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const displayItems = intelligenceItems;
  const pendingCount = displayItems.filter(item => !item.dismissed_at).length;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      HIGH: 'bg-red-100 text-red-700',
      MEDIUM: 'bg-yellow-100 text-yellow-700',
      LOW: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${styles[priority] || styles.LOW}`}>
        {priority}
      </span>
    );
  };

  const handleCopyPrompt = async (item) => {
    const promptText = `${item.description}\n\n${item.recommendations.map((r, i) => `${i + 1}. ${r}`).join('\n')}\n\n${item.conclusion}`;
    await navigator.clipboard.writeText(promptText);
    // Log action
    try {
      const email = getSuperAdminEmail();
      await fetch(`/api/ali/super-admin/intelligence/${item.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'prompt_copied', email })
      });
    } catch (error) {
      console.error('Error logging action:', error);
    }
  };

  const handleViewCompany = () => {
    const email = getSuperAdminEmail();
    const base = '/ali/super-admin/tenants';
    const url = email ? `${base}?email=${encodeURIComponent(email)}` : base;
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleMarkResolved = async (itemId) => {
    try {
      const email = getSuperAdminEmail();
      await fetch(`/api/ali/super-admin/intelligence/${itemId}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      setIntelligenceItems(items => items.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error dismissing item:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] ali-system">
      <SuperAdminNav activeTab="intelligence" />
      
      <div className="pt-8 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-[28px] font-semibold text-black/[0.87] mb-1">Intelligence Inbox</h1>
            <p className="text-[14px] text-black/[0.6]">Rule-based signals from leadership assessment data — perception gaps, low scores, and stale deployments</p>
          </div>
          <span className="px-3 py-1.5 rounded-full text-[13px] font-medium bg-blue-100 text-blue-700">
            {pendingCount} Pending
          </span>
        </div>

        {/* Intelligence Cards */}
        <div className="space-y-4">
          {displayItems.length === 0 && (
            <div className="bg-white rounded-xl border border-black/[0.12] p-8 text-center text-[14px] text-black/[0.6]">
              No intelligence items right now. The system checks weekly for leadership gaps, stale deployments, and low-scoring accounts — new items will appear here as real signals come in.
            </div>
          )}
          {displayItems.map((item) => (
            <div key={item.id} className="bg-white rounded-xl border border-black/[0.12] p-6 shadow-sm">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[18px] font-semibold text-black/[0.87]">{item.companyName}</h3>
                <div className="flex items-center gap-2">
                  {getPriorityBadge(item.priority)}
                  <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700">
                    {item.type}
                  </span>
                </div>
              </div>

              {/* Details */}
              <p className="text-[13px] text-black/[0.6] mb-4">
                Leader: {item.leaderName} • {formatDate(item.timestamp)}
              </p>

              {/* Metrics */}
              {item.metrics && (
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700">
                    ALI: {item.metrics.aliScore}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
                    {item.metrics.pattern}
                  </span>
                  {item.metrics.gap && (
                    <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-red-100 text-red-700">
                      Gap: {item.metrics.gap} pts
                    </span>
                  )}
                </div>
              )}

              {/* Description */}
              <p className="text-[14px] text-black/[0.87] mb-4">
                {item.description}
              </p>

              {/* Recommendations */}
              {item.recommendations && item.recommendations.length > 0 && (
                <ol className="list-decimal list-inside space-y-2 mb-4 text-[14px] text-black/[0.87]">
                  {item.recommendations.map((rec, idx) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ol>
              )}

              {/* Conclusion */}
              <p className="text-[14px] text-black/[0.87] mb-4">
                {item.conclusion}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-black/[0.12]">
                <button
                  onClick={() => handleCopyPrompt(item)}
                  className="px-4 py-2 bg-[#2563eb] text-white rounded-lg text-[14px] font-semibold hover:bg-[#1d4ed8] transition-colors flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy Prompt
                </button>
                <button
                  onClick={() => handleViewCompany(item.companyName)}
                  className="px-4 py-2 border border-black/[0.12] bg-white text-black/[0.87] rounded-lg text-[14px] font-semibold hover:bg-black/[0.04] transition-colors flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Company
                </button>
                <button
                  onClick={() => handleMarkResolved(item.id)}
                  className="text-[14px] text-black/[0.6] hover:text-black/[0.87] transition-colors"
                >
                  Mark as Resolved
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminIntelligence;

